import { ApiResult, PaginatedResponse, request, safeRequest } from '@synergy/api';
import { Media } from '@synergy/models';

import { BankAccount } from '../models/bank-accounts';
import { BankAccountErrorCode } from '../../../../models/bank-account-error-code';
import { BankAccountRequest } from './models/bank-account-request';
import { BlacklistedSupplier } from '../models/blacklisted-supplier';
import { CustomerDetail } from '../models/customer-detail';
import { CustomerDetailOverviewRequest } from './models/customer-detail-overview-request';
import { EmailErrorCode } from '../../../../models/email-error-code';
import { EmailErrorHandler } from '../../../../api/email-error-handler';
import { OverviewBankAccountErrorHandler } from '../../../../api/overview-bank-account-error-handler';
import { ParentCustomer } from '../models/parent-supplier';

export class CustomerDetailOverviewApi {
    constructor(protected baseUrl: string) {}

    public get(customerId: number): Promise<ApiResult<CustomerDetail>> {
        return safeRequest(
            () => request.get<CustomerDetail>(`${this.baseUrl}/customers/${customerId}/details`),
            response => response.data
        );
    }

    public patchDetail(
        customerId: number,
        changes: CustomerDetailOverviewRequest
    ): Promise<ApiResult<CustomerDetail, EmailErrorCode>> {
        return safeRequest(
            () => request.patch(`${this.baseUrl}/customers/${customerId}/details`, changes),
            response => response.data,
            EmailErrorHandler
        );
    }

    public createDetail(changes: CustomerDetailOverviewRequest): Promise<ApiResult<CustomerDetail, EmailErrorCode>> {
        return safeRequest(
            () =>
                request.post(`${this.baseUrl}/customers`, changes, {
                    params: { isSync: true },
                }),
            response => response.data,
            EmailErrorHandler
        );
    }

    public deleteFtpUser(customerId: number): Promise<ApiResult<null>> {
        return safeRequest(
            () => request.delete(`${this.baseUrl}/customers/${customerId}/ftp-user`),
            () => null
        );
    }

    public addBankAccount(
        customerId: number,
        bankAccount: BankAccountRequest
    ): Promise<ApiResult<BankAccount, BankAccountErrorCode>> {
        return safeRequest(
            () => request.post(`${this.baseUrl}/customers/${customerId}/add-bank-account`, { ...bankAccount }),
            response => response.data,
            OverviewBankAccountErrorHandler
        );
    }

    public updateBankAccount(
        customerId: number,
        bankAccount: BankAccountRequest,
        id: number
    ): Promise<ApiResult<BankAccount, BankAccountErrorCode>> {
        return safeRequest(
            () =>
                request.patch(`${this.baseUrl}/customers/${customerId}/update-bank-account/${id}`, {
                    ...bankAccount,
                }),
            response => response.data,
            OverviewBankAccountErrorHandler
        );
    }

    public deleteBankAccount(customerId: number, bankAccountId: number): Promise<ApiResult<null>> {
        return safeRequest(
            () => request.delete(`${this.baseUrl}/customers/${customerId}/remove-bank-account/${bankAccountId}`),
            () => null
        );
    }

    public uploadFile(customerId: number, bankAccountId: number, file: File): Promise<ApiResult<Media>> {
        return safeRequest(
            () => {
                const data = new FormData();
                data.append('file', file, file.name);

                const config = { headers: { 'content-type': 'multipart/form-data' } };

                return request.post(
                    `${this.baseUrl}/customers/${customerId}/upload-bank-account-media/${bankAccountId}`,
                    data,
                    config
                );
            },
            response => response.data
        );
    }

    public uploadProfileImage(customerId: number, file: File): Promise<ApiResult<Media>> {
        return safeRequest(
            () => {
                const data = new FormData();
                data.append('file', file, file.name);

                const config = { headers: { 'content-type': 'multipart/form-data' } };

                return request.post(`${this.baseUrl}/customers/${customerId}/upload-image`, data, config);
            },
            response => response.data
        );
    }

    public removeProfileImage(customerId: number): Promise<ApiResult<null>> {
        return safeRequest(
            () => request.delete(`${this.baseUrl}/customers/${customerId}/remove-image`),
            () => null
        );
    }

    public getParentCustomers(
        customerId: number,
        params: URLSearchParams = new URLSearchParams()
    ): Promise<ApiResult<PaginatedResponse<ParentCustomer>>> {
        return safeRequest(
            () => request.get(`${this.baseUrl}/customers/${customerId}/search-parent`, { params }),
            response => response.data
        );
    }

    public searchBlacklistedSuppliers(
        customerId: number,
        params: URLSearchParams = new URLSearchParams()
    ): Promise<ApiResult<BlacklistedSupplier[]>> {
        return safeRequest(
            () =>
                request.get(`${this.baseUrl}/customers/${customerId}/search-possible-blacklist-suppliers`, {
                    params,
                }),
            response => response.data
        );
    }
}
